import * as THREE from 'three/webgpu';
import type { ModelPointAxisCandidate, ModelPointAxisCandidates } from '../../types/sketch/ModelPointAxisTracking';
import type { SketchSessionReferencePointManagerHost } from '../../types/manager/SketchSessionReferencePointManager';
import type {
    VisibleModelEdgeHoverOptions,
    VisibleModelEdgeProjectionRecord,
    VisibleModelEdgeReferencePointKind,
} from '../../types/sketch/VisibleModelEdgeProjection';
import { PointsMaterialFactory } from '../material';
import { isModelReferencePointSnapEnabled } from '../utils';
import { isFeatureResultMesh } from '../mesh/feature';
import type { FeatureResultMesh } from '../mesh/feature';
import { SketchPointKind, SketchPointObject } from '../mesh/sketch';
import type { ReferencePlane } from '../reference';
import {
    MeshBvhModelEdgeOcclusionQuery,
    ModelEdgeSnapHighlight,
    ModelPointAxisIndex,
    VisibleModelEdgeProjectionIndex,
    VisibleModelEdgeProjectionService,
} from '../sketch/model-snap';

/** 草图会话参考点渲染顺序。 */
const SKETCH_SESSION_REFERENCE_POINT_RENDER_ORDER = 1000;

/**
 * 草图会话参考点管理器。
 *
 * 职责：
 * 1. 在活动草图会话内创建并维护世界原点投影；
 * 2. 按需缓存当前草图平面的可见模型边投影；
 * 3. 唤醒可见投影边后动态创建端点、中点、中心与象限点，并在空白区保持到新边接管；
 * 4. 为绘制、移动实体、复制实体与拖点捕捉提供统一只读点集合；
 * 5. 在参考面变化、会话结束或 Viewer 释放时统一回收场景对象与 BVH。
 *
 * 这些点只属于会话参考层，不进入 SketchGroup、持久化、删除或拖拽链路。
 */
export class SketchSessionReferencePointManager {
    /** 当前模型原点投影点。 */
    private originPoint: SketchPointObject | null = null;

    /** 当前唤醒模型边的动态参考点。 */
    private readonly modelProjectionPoints: SketchPointObject[] = [];

    /** 可见模型边正交投影几何服务。 */
    private readonly visibleModelEdgeProjectionService = new VisibleModelEdgeProjectionService();

    /** 可见投影子线段 LineSegmentsBVH 索引。 */
    private readonly visibleModelEdgeProjectionIndex = new VisibleModelEdgeProjectionIndex();

    /** 当前渲染器画布像素尺寸缓存。 */
    private readonly viewportSize = new THREE.Vector2();

    /** 草图参考面世界原点缓存。 */
    private readonly referencePlaneOrigin = new THREE.Vector3();

    /** 当前相机世界坐标缓存。 */
    private readonly cameraWorldPosition = new THREE.Vector3();

    /** 最后一条被指针唤醒的可见模型投影边。 */
    private awakenedModelEdgeRecord: VisibleModelEdgeProjectionRecord | null = null;

    /** 与当前唤醒参考点共用生命周期的源模型边高亮。 */
    private modelEdgeHighlight: ModelEdgeSnapHighlight | null = null;

    /** 不依赖移入显示状态的完整投影点索引。 */
    private readonly modelPointAxisIndex = new ModelPointAxisIndex();
    /** 当前与点轴向追踪使用的模型和参考点类型开关。 */
    private readonly modelPointAxisOptions: VisibleModelEdgeHoverOptions = {
        modelGeometryEnabled: false, endpointEnabled: false, midpointEnabled: false, centerEnabled: false, quadrantEnabled: false,
    };
    /** 只为当前最终命中的轴向参考点维护视觉对象，不加入普通点捕捉白名单。 */
    private readonly axisTrackingPoints = new Map<ModelPointAxisCandidate, SketchPointObject>();
    /** 水平和竖直候选可能来自不同模型边，按源记录去重高亮。 */
    private readonly axisTrackingHighlights = new Map<VisibleModelEdgeProjectionRecord, ModelEdgeSnapHighlight>();

    /** 当前动态参考点使用的端点门禁。 */
    private awakenedEndpointEnabled = false;

    /** 当前动态参考点使用的中点门禁。 */
    private awakenedMidpointEnabled = false;

    /** 当前动态参考点使用的中心点门禁。 */
    private awakenedCenterEnabled = false;

    /** 当前动态参考点使用的象限点门禁。 */
    private awakenedQuadrantEnabled = false;

    /** 当前草图会话是否已经按需构建可见模型边投影索引。 */
    private isVisibleModelEdgeProjectionIndexReady = false;

    /** 当前可见性缓存对应的草图平面观察侧。 */
    private visibleModelEdgeProjectionViewSide: 1 | -1 | null = null;

    /**
     * @param host 场景与模型拓扑宿主端口。
     */
    public constructor(private readonly host: SketchSessionReferencePointManagerHost) {}

    /**
     * 按当前活动草图参考面刷新全部会话参考点。
     *
     * 刷新是参考面切换和上游模型变化的重建边界；旧对象先统一回收，避免场景残留。
     */
    public refresh(): void {
        this.destroy();

        const scene = this.host.getScene();
        const referencePlane = this.host.getReferencePlane();
        if (!scene || !referencePlane) {
            return;
        }

        this.originPoint = this.createOriginPoint(referencePlane);
        scene.add(this.originPoint);

        this.host.requestRender();
    }

    /**
     * 获取当前模型原点投影点。
     *
     * @returns 会话未激活时返回 null。
     */
    public getOriginPoint(): SketchPointObject | null {
        return this.originPoint;
    }

    /**
     * 获取当前全部会话参考点。
     *
     * 返回新数组以阻止调用方修改管理器内部集合；顺序固定为原点、当前唤醒模型边参考点。
     *
     * @returns 只读会话参考点数组。
     */
    public getPointObjects(): readonly SketchPointObject[] {
        return [
            ...(this.originPoint ? [this.originPoint] : []),
            ...this.modelProjectionPoints,
        ];
    }

    /**
     * 按当前鼠标位置更新可见模型边悬停及其动态捕捉参考点。
     *
     * 首次命中 LineSegmentsBVH 中的可见投影子段时唤醒源拓扑边，并创建该边允许的
     * 端点、中点、中心和象限点。指针移入空白区时保留当前唤醒边，命中另一条投影边时才切换；
     * 关闭门禁、参考面/模型失效或会话结束时立即回收。
     *
     * @param screenPosition 指针在渲染器画布内的像素坐标。
     * @param options 模型几何及四类参考点捕捉门禁。
     * @returns 当前动态模型参考点只读快照。
     */
    public updateVisibleModelEdgeHover(
        screenPosition: THREE.Vector2,
        options: VisibleModelEdgeHoverOptions,
    ): readonly SketchPointObject[] {
        const referencePlane = this.host.getReferencePlane();
        const camera = this.host.getCamera();
        if (!options.modelGeometryEnabled || !referencePlane || !camera) {
            this.clearModelProjectionPoints();
            return this.modelProjectionPoints;
        }

        // 移入与轴向查询共享按需投影入口，模型点候选不再由移入事件负责初始化。
        if (!this.ensureVisibleModelProjection(referencePlane, camera)) return this.modelProjectionPoints;

        this.host.getViewportSize(this.viewportSize);
        const hit = this.visibleModelEdgeProjectionIndex.pick({
            screenPosition,
            camera,
            viewportSize: this.viewportSize,
            referencePlane,
            thresholdPx: 12,
        });
        if (!hit) {
            // 与正式草图圆/圆弧的动态点一致：移入空白区仍保留最后唤醒边，便于指针移向边内的中心或象限点。
            const awakenedRecord = this.awakenedModelEdgeRecord;
            if (
                awakenedRecord
                && !this.isAwakenedModelEdgeStateCurrent(
                    awakenedRecord,
                    options,
                )
            ) {
                this.activateModelEdgeRecord(
                    awakenedRecord,
                    referencePlane,
                    options,
                );
            }
            return this.modelProjectionPoints;
        }

        if (this.isAwakenedModelEdgeStateCurrent(hit.record, options)) {
            return this.modelProjectionPoints;
        }

        this.activateModelEdgeRecord(hit.record, referencePlane, options);
        return this.modelProjectionPoints;
    }

    /**
     * 激活一条可见模型投影边，并按当前门禁重建其动态参考点。
     *
     * @param record 待唤醒的权威投影边记录。
     * @param referencePlane 当前草图参考面。
     * @param options 模型几何及四类参考点捕捉门禁。
     */
    private activateModelEdgeRecord(
        record: VisibleModelEdgeProjectionRecord,
        referencePlane: ReferencePlane,
        options: VisibleModelEdgeHoverOptions,
    ): void {
        this.clearModelProjectionPoints();
        this.awakenedModelEdgeRecord = record;
        this.awakenedEndpointEnabled = options.endpointEnabled;
        this.awakenedMidpointEnabled = options.midpointEnabled;
        this.awakenedCenterEnabled = options.centerEnabled;
        this.awakenedQuadrantEnabled = options.quadrantEnabled;

        // 高亮使用源模型的可见边段；参考点仍使用草图平面上的投影坐标。
        this.modelEdgeHighlight = new ModelEdgeSnapHighlight(record);
        // 同一条源边已有移入高亮时，暂时隐藏轴向高亮，避免重复叠画。
        const trackingHighlight = this.axisTrackingHighlights.get(record);
        if (trackingHighlight) trackingHighlight.visible = false;

        record.referencePoints.forEach((referencePoint, index) => {
            if (!isModelReferencePointSnapEnabled(referencePoint.kind, options)) {
                return;
            }

            const point = this.createModelProjectionPoint(
                referencePlane,
                referencePoint.kind,
                referencePoint.projectedPosition,
                index,
            );
            this.host.getScene()?.add(point);
            this.modelProjectionPoints.push(point);
        });

        // 即使所有参考点子开关都关闭，唤醒的模型边仍需要立即显示高亮。
        this.host.requestRender();
    }

    /**
     * 判断当前唤醒边及参考点门禁是否已与请求一致。
     *
     * @param record 当前命中或最后唤醒的投影边记录。
     * @param options 当前模型捕捉门禁。
     * @returns 边身份和四类参考点门禁都一致时返回 true。
     */
    private isAwakenedModelEdgeStateCurrent(
        record: VisibleModelEdgeProjectionRecord,
        options: VisibleModelEdgeHoverOptions,
    ): boolean {
        const awakenedRecord = this.awakenedModelEdgeRecord;
        return awakenedRecord !== null
            && awakenedRecord.sourceMesh === record.sourceMesh
            && awakenedRecord.edgeIndex === record.edgeIndex
            && this.awakenedEndpointEnabled === options.endpointEnabled
            && this.awakenedMidpointEnabled === options.midpointEnabled
            && this.awakenedCenterEnabled === options.centerEnabled
            && this.awakenedQuadrantEnabled === options.quadrantEnabled;
    }

    /**
     * 清除当前唤醒模型边及其动态参考点。
     *
     * 该入口供捕捉总开关关闭、绘制工具退出和移动命令结束时同步清理瞬态视觉状态。
     */
    public clearVisibleModelEdgeHover(): void {
        this.clearModelProjectionPoints();
    }

    /**
     * 使当前可见模型边投影缓存失效。
     *
     * 模型显示模式、结果体拓扑或实体替换显示状态变化后调用。世界原点不受影响；下一次模型
     * 捕捉请求会按新的显示边集合、模型网格和观察侧重新构建 MeshBVH 查询与 LineSegmentsBVH。
     */
    public invalidateVisibleModelEdgeProjection(): void {
        this.clearModelProjectionPoints();
        this.clearModelPointAxisTracking();
        this.modelPointAxisIndex.clear();
        this.visibleModelEdgeProjectionIndex.dispose();
        this.isVisibleModelEdgeProjectionIndexReady = false;
        this.visibleModelEdgeProjectionViewSide = null;
    }

    /**
     * 判断点是否由当前草图会话参考点管理器托管。
     *
     * @param object 待判断场景对象。
     * @returns 属于当前会话参考点时返回 true。
     */
    public owns(object: THREE.Object3D | null | undefined): object is SketchPointObject {
        return object === this.originPoint || this.modelProjectionPoints.includes(object as SketchPointObject);
    }

    /**
     * 销毁当前会话全部参考点。
     *
     * 材质由 PointsMaterialFactory 全局复用，此处只解除场景挂载和对象引用，禁止释放共享材质。
     */
    public destroy(): void {
        this.clearModelPointAxisTracking();
        this.modelPointAxisIndex.clear();
        this.clearModelEdgeHighlight();
        const scene = this.host.getScene();
        const points = this.getPointObjects();

        if (points.length > 0) {
            this.host.onBeforeDestroy(points);
        }

        points.forEach((point) => {
            scene?.remove(point);
            point.removeFromParent();
        });

        this.originPoint = null;
        this.modelProjectionPoints.length = 0;
        this.awakenedModelEdgeRecord = null;
        this.awakenedEndpointEnabled = false;
        this.awakenedMidpointEnabled = false;
        this.awakenedCenterEnabled = false;
        this.awakenedQuadrantEnabled = false;
        this.visibleModelEdgeProjectionIndex.dispose();
        this.isVisibleModelEdgeProjectionIndexReady = false;
        this.visibleModelEdgeProjectionViewSide = null;

        if (points.length > 0) {
            this.host.requestRender();
        }
    }

    /**
     * 释放管理器资源。
     */
    public dispose(): void {
        this.destroy();
    }

    /**
     * 创建世界原点在当前草图参考面上的投影点。
     *
     * @param referencePlane 当前草图参考面。
     * @returns 红色会话原点投影点。
     */
    private createOriginPoint(referencePlane: ReferencePlane): SketchPointObject {
        const worldOrigin = new THREE.Vector3(0, 0, 0);
        const originLocal = referencePlane.worldToLocal(worldOrigin);
        const originPosition = referencePlane.localToWorld(originLocal);
        const point = new SketchPointObject({
            position: originPosition,
            pointKind: SketchPointKind.SketchPoint,
            material: PointsMaterialFactory.getDrawingSketchOriginSpriteMaterial(),
            referencePlane,
            isAuxiliaryObject: true,
        });

        point.name = '草图会话原点投影';
        point.isDrawingSketchOrigin = true;
        point.renderOrder = SKETCH_SESSION_REFERENCE_POINT_RENDER_ORDER;
        return point;
    }

    /**
     * 创建可见模型边动态投影参考点。
     *
     * @param referencePlane 当前草图参考面。
     * @param kind 投影点类型。
     * @param position 已投影世界坐标。
     * @param index 本轮投影点序号。
     * @returns 绿色会话模型投影点。
     */
    private createModelProjectionPoint(
        referencePlane: ReferencePlane,
        kind: VisibleModelEdgeReferencePointKind,
        position: THREE.Vector3,
        index: number,
    ): SketchPointObject {
        const point = new SketchPointObject({
            position: position.clone(),
            pointKind: SketchPointKind.SketchPoint,
            material: PointsMaterialFactory.getDrawingSketchReferencePointSpriteMaterial(),
            referencePlane,
            isAuxiliaryObject: true,
        });

        point.isSketchSessionReferencePoint = true;
        point.isSketchSessionModelEdgeEndpoint = kind === 'endpoint';
        point.isSketchSessionModelEdgeMidpoint = kind === 'midpoint';
        point.isSketchSessionModelEdgeCenter = kind === 'center';
        point.isSketchSessionModelEdgeQuadrant = kind === 'quadrant';
        point.restoreDefaultScale();
        point.name = this.resolveModelProjectionPointName(kind, index);
        point.renderOrder = SKETCH_SESSION_REFERENCE_POINT_RENDER_ORDER;
        return point;
    }

    /**
     * 解析动态模型参考点的可定位名称。
     *
     * @param kind 模型参考点语义。
     * @param index 当前悬停边内的参考点序号。
     * @returns 中文场景对象名称。
     */
    private resolveModelProjectionPointName(
        kind: VisibleModelEdgeReferencePointKind,
        index: number,
    ): string {
        switch (kind) {
            case 'endpoint':
                return `可见模型边端点${index + 1}`;
            case 'midpoint':
                return `可见模型边中点${index + 1}`;
            case 'center':
                return `可见模型边中心点${index + 1}`;
            case 'quadrant':
                return `可见模型边象限点${index + 1}`;
        }
    }

    /**
     * 重建当前草图会话的可见模型边投影 LineSegmentsBVH。
     *
     * @param scene 当前主场景。
     * @param referencePlane 当前活动草图参考面。
     */
    private rebuildVisibleModelEdgeProjectionIndex(
        scene: THREE.Scene,
        referencePlane: ReferencePlane,
    ): void {
        const camera = this.host.getCamera();
        if (!camera) {
            this.visibleModelEdgeProjectionIndex.rebuild([]);
            return;
        }

        const resultMeshes: FeatureResultMesh[] = this.host.collectVisibleModelEdgeProjectionSources?.() ?? [];
        if (!this.host.collectVisibleModelEdgeProjectionSources) {
            // 独立 SDK 宿主保持普通结果体路径；Viewer 通过端口补齐实例化模型的语义来源。
            scene.traverse((object) => {
                if (
                    isFeatureResultMesh(object)
                    && object.isTopologyDisplayEdgesVisible()
                    && this.isObjectActuallyVisible(object)
                ) {
                    resultMeshes.push(object);
                }
            });
        }
        if (resultMeshes.length === 0) {
            this.visibleModelEdgeProjectionIndex.rebuild([]);
            return;
        }

        const cameraSide = this.resolveReferencePlaneViewSide(referencePlane, camera);
        const viewDirection = referencePlane.normal.clone().multiplyScalar(cameraSide).normalize();
        const occlusionQuery = new MeshBvhModelEdgeOcclusionQuery({
            resultMeshes,
            viewDirection,
            observerPosition: camera.getWorldPosition(new THREE.Vector3()),
        });
        const records = this.visibleModelEdgeProjectionService.build({
            resultMeshes,
            referencePlane,
            occlusionQuery,
        });
        this.visibleModelEdgeProjectionIndex.rebuild(records);
        this.modelPointAxisIndex.rebuild(records);
    }

    /**
     * 更新轴向查询门禁，由 PointSnapManager 统一传入，禁止策略自行绕过捕捉设置。
     * @param options 当前模型与参考点类型设置。
     */
    public configureModelPointAxisTracking(options: VisibleModelEdgeHoverOptions): void {
        Object.assign(this.modelPointAxisOptions, options);
        if (!options.modelGeometryEnabled) this.clearModelPointAxisTracking();
    }

    /**
     * 查询无需移入即可参与水平/竖直对齐的模型投影点。
     * @param screenPosition 渲染器像素坐标。
     * @param thresholdPx 轴向允许偏差。
     * @param excludedIds 不可捕捉的源对象集合。
     * @returns 两个最佳候选；捕捉关闭或会话无效时返回 null。
     */
    public queryModelPointAxisCandidates(screenPosition: THREE.Vector2, thresholdPx: number,
        excludedIds?: ReadonlySet<string>): ModelPointAxisCandidates | null {
        const referencePlane = this.host.getReferencePlane();
        const camera = this.host.getCamera();
        if (!this.modelPointAxisOptions.modelGeometryEnabled || !referencePlane || !camera
            || !this.ensureVisibleModelProjection(referencePlane, camera)) return null;
        this.host.getViewportSize(this.viewportSize);
        return this.modelPointAxisIndex.query(screenPosition, camera, this.viewportSize, thresholdPx, this.modelPointAxisOptions, excludedIds);
    }

    /**
     * 保证边拾取与点轴向查询使用同一份可见模型投影。
     * @param referencePlane 当前草图平面。
     * @param camera 当前相机。
     * @returns 是否有有效场景可建立查询。
     */
    private ensureVisibleModelProjection(referencePlane: ReferencePlane, camera: THREE.Camera): boolean {
        const currentViewSide = this.resolveReferencePlaneViewSide(referencePlane, camera);
        if (this.isVisibleModelEdgeProjectionIndexReady && this.visibleModelEdgeProjectionViewSide === currentViewSide) return true;
        this.invalidateVisibleModelEdgeProjection();
        const scene = this.host.getScene();
        if (!scene) return false;
        this.rebuildVisibleModelEdgeProjectionIndex(scene, referencePlane);
        this.isVisibleModelEdgeProjectionIndexReady = true;
        this.visibleModelEdgeProjectionViewSide = currentViewSide;
        return true;
    }

    /**
     * 仅为最终胜出的模型参考点同步视觉反馈，正式草图点胜出时对应参数为 null。
     * @param horizontal 水平参考点。
     * @param vertical 竖直参考点。
     */
    public showModelPointAxisTracking(horizontal: ModelPointAxisCandidate | null, vertical: ModelPointAxisCandidate | null): void {
        const referencePlane = this.host.getReferencePlane();
        if (!referencePlane) { this.clearModelPointAxisTracking(); return; }
        let changed = false;
        for (const [candidate, point] of this.axisTrackingPoints) {
            if (candidate === horizontal || candidate === vertical) continue;
            point.removeFromParent();
            this.axisTrackingPoints.delete(candidate);
            changed = true;
        }
        for (const [record, highlight] of this.axisTrackingHighlights) {
            if (record === horizontal?.record || record === vertical?.record) continue;
            highlight.dispose();
            this.axisTrackingHighlights.delete(record);
            changed = true;
        }
        // 最多两个候选；用固定循环避免在每次指针移动时创建候选数组。
        for (let index = 0; index < 2; index += 1) {
            const candidate = index === 0 ? horizontal : vertical;
            if (!candidate) continue;
            if (!this.axisTrackingPoints.has(candidate)) {
                const point = this.createModelProjectionPoint(referencePlane, candidate.referencePoint.kind, candidate.referencePoint.projectedPosition, index);
                this.host.getScene()?.add(point);
                this.axisTrackingPoints.set(candidate, point);
                changed = true;
            }
            if (!this.axisTrackingHighlights.has(candidate.record)) {
                const highlight = new ModelEdgeSnapHighlight(candidate.record);
                highlight.visible = candidate.record !== this.awakenedModelEdgeRecord;
                this.axisTrackingHighlights.set(candidate.record, highlight);
                changed = true;
            }
        }
        if (changed) this.host.requestRender();
    }

    /** 清理轴向追踪的点和源边高亮，保留可复用的几何候选索引。 */
    public clearModelPointAxisTracking(): void {
        const changed = this.axisTrackingPoints.size > 0 || this.axisTrackingHighlights.size > 0;
        for (const point of this.axisTrackingPoints.values()) point.removeFromParent();
        for (const highlight of this.axisTrackingHighlights.values()) highlight.dispose();
        this.axisTrackingPoints.clear();
        this.axisTrackingHighlights.clear();
        if (changed) this.host.requestRender();
    }

    /**
     * 判断相机位于当前草图参考面的正法向侧还是负法向侧。
     *
     * @param referencePlane 当前草图参考面。
     * @param camera 当前视口相机。
     * @returns 正法向侧返回 1，负法向侧返回 -1。
     */
    private resolveReferencePlaneViewSide(
        referencePlane: ReferencePlane,
        camera: THREE.Camera,
    ): 1 | -1 {
        referencePlane.copyOriginWorldTo(this.referencePlaneOrigin);
        camera.getWorldPosition(this.cameraWorldPosition);
        return this.cameraWorldPosition.sub(this.referencePlaneOrigin).dot(referencePlane.normal) >= 0 ? 1 : -1;
    }

    /**
     * 回收当前唤醒模型边产生的动态参考点。
     */
    private clearModelProjectionPoints(): void {
        this.clearModelEdgeHighlight();
        if (this.modelProjectionPoints.length === 0) {
            this.awakenedModelEdgeRecord = null;
            this.awakenedEndpointEnabled = false;
            this.awakenedMidpointEnabled = false;
            this.awakenedCenterEnabled = false;
            this.awakenedQuadrantEnabled = false;
            return;
        }

        const points = [...this.modelProjectionPoints];
        this.host.onBeforeDestroy(points);
        points.forEach((point) => {
            this.host.getScene()?.remove(point);
            point.removeFromParent();
        });
        this.modelProjectionPoints.length = 0;
        this.awakenedModelEdgeRecord = null;
        this.awakenedEndpointEnabled = false;
        this.awakenedMidpointEnabled = false;
        this.awakenedCenterEnabled = false;
        this.awakenedQuadrantEnabled = false;
        this.host.requestRender();
    }

    /**
     * 回收源边高亮；无参考点时也必须请求重绘，避免画面残留。
     */
    private clearModelEdgeHighlight(): void {
        if (!this.modelEdgeHighlight) {
            return;
        }
        this.modelEdgeHighlight.dispose();
        this.modelEdgeHighlight = null;
        // 移入反馈结束后，仍被轴向追踪引用的同源边继续保持可见。
        if (this.awakenedModelEdgeRecord) {
            const trackingHighlight = this.axisTrackingHighlights.get(this.awakenedModelEdgeRecord);
            if (trackingHighlight) trackingHighlight.visible = true;
        }
        this.host.requestRender();
    }

    /**
     * 判断对象及其父级是否全部可见。
     *
     * @param object 待判断场景对象。
     * @returns 对象真实可见时返回 true。
     */
    private isObjectActuallyVisible(object: THREE.Object3D): boolean {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (!current.visible) {
                return false;
            }
            current = current.parent;
        }
        return true;
    }
}
